/**
 * Die Zeitsteuerung in der App (S62; 25-zeitsteuerung.md): the business's choice in the account, and
 * what the app shows only with it. Without it („Aus“, the default) not one new button (S38).
 */
import { DAY_MS, TIME_WORDS, dateAfter, localOf, timingAtLeast } from "@speisekarte/core";
import type { Timing } from "@speisekarte/core";
import type { Data } from "./store.ts";

export const timingOf = (data: Pick<Data, "settings"> | null | undefined): Timing => data?.settings.timing ?? "off";
/** Stufe 1: sections at times on the net, changes planned for later. */
export const timedOn = (data: Pick<Data, "settings"> | null | undefined) => timingAtLeast(timingOf(data), "standard");
/** Stufe 2: folders, the weekly plan, exceptions. */
export const extendedOn = (data: Pick<Data, "settings"> | null | undefined) => timingAtLeast(timingOf(data), "extended");

/**
 * What going back to less means for the guests (Z0, § 7.8 of the draft), one sentence each, for the
 * question before; empty: nothing to lose, no question.
 */
export function lowerEffects(data: Data, next: Timing): string[] {
  const now = timingOf(data);
  if (timingAtLeast(next, now)) return [];
  const out: string[] = [];
  // From „Erweitert“: the weekly plan rests, „Sonst“ applies always.
  if (now === "extended" && (data.folders ?? []).length) out.push(`Der Wochenplan ruht: Gäste sehen dann immer die Mappe „${data.settings.otherwiseName || "Abend"}“.`);
  // To „Aus“: what is planned goes (§ 7.8); to „Standard“ what is planned for a folder.
  const planned = (data.settings.planned ?? []).filter((p) => next === "off" || p.folder);
  if (planned.length) out.push(planned.length === 1 ? "1 geplanter Wechsel entfällt." : `${planned.length} geplante Wechsel entfallen.`);
  // To „Aus“ (Stufe 1 rests): the temporary sections of the menu on the net stand there always.
  if (next === "off") {
    const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
    const temp = (pub?.sections ?? []).filter((s) => s.web && s.items.length);
    if (temp.length) out.push(`Gäste sehen ${temp.map((s) => `„${s.title || "Abschnitt"}“`).join(", ")} dann immer, auch außerhalb ${temp.length === 1 ? "seiner" : "ihrer"} Zeit; gedruckt ohne Zeitzeile.`);
  }
  return out;
}

const DAYS = TIME_WORDS.de.days;

/** A time of day as the app says it: „6:00“, „14:30“ (inputs keep „06:00“). */
export const uhr = (minute: number) => `${Math.floor(minute / 60)}:${String(minute % 60).padStart(2, "0")}`;

/** A moment as the app says it beside now: „14:30“ today, „morgen 11:30“, „Mo 11:30“, „Mo 9.11. 11:30“ beyond a week. */
export function whenText(t: number, now: number): string {
  const a = localOf(now);
  const b = localOf(t);
  const clock = uhr(b.minute);
  if (a.date === b.date) return clock;
  if (dateAfter(a.date, 1) === b.date) return `morgen ${clock}`;
  if (t - now < 6 * DAY_MS) return `${DAYS[b.weekday]} ${clock}`;
  const [, m, d] = b.date.split("-");
  return `${DAYS[b.weekday]} ${Number(d)}.${Number(m)}. ${clock}`;
}

